import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Filters } from 'src/hooks/types'
import { CartDetails } from 'src/redux/cart/types'
import { queryClient } from 'src/routes/AppProviders'
import { Cart, Delivery, PostalAddress, Product, ProductCategory } from 'src/types/types'
import usePaginatedQuery from '../../../hooks/usePaginatedQuery'
import {
  addToCart,
  clearCart,
  createCustomerRequest,
  createOrderRequest,
  getCart,
  getCustomerDetailsRequest,
  getDeliveryDetails,
  getOrderDetailsRequest,
  getPostalCodes,
  getProductCategoriesRequest,
  getProductTagsRequest,
  getProductsRequest,
  removeCartItem,
  updateCartItem,
  updateCustomerRequest,
} from '../requests'
import { WcQueryParams } from '../types'
import {
  createWCQueryParams,
  mapCartDetailsToOrderRequest,
  mapCartDetailsToUpdateCustomerRequest,
} from '../utils'

export interface UseProductsParams {
  searchTerm?: string
  filters?: Filters
}

export function useProducts(params: WcQueryParams) {
  const queryParams = createWCQueryParams(params)

  return usePaginatedQuery<Product>({
    key: ['products', JSON.stringify(queryParams)],
    fetchFunction: (_, page) => getProductsRequest({ ...queryParams, page: page + 1 }),
  })
}

export function useProductCategories() {
  const { data, ...rest } = usePaginatedQuery({
    key: ['categories'],
    fetchFunction: (_, page) => getProductCategoriesRequest(page + 1),
  })

  const categoryFilterOptions =
    data?.map((category) => ({
      id: category.id.toString(),
      label: category.name,
    })) ?? []

  return { data: categoryFilterOptions, ...rest }
}

export function useProductTags(params: WcQueryParams = {}) {
  const queryParams = createWCQueryParams(params)

  return usePaginatedQuery({
    key: ['tags', JSON.stringify(params)],
    fetchFunction: (_, page) => getProductTagsRequest({ ...queryParams, page: page + 1 }),
  })
}

export function useCreateOrder(): UseMutationResult<unknown, Error, any, unknown> {
  return useMutation<unknown, Error, any>((cartDetails: CartDetails) => {
    const body = mapCartDetailsToOrderRequest(cartDetails)
    return createOrderRequest(body)
  })
}

export function useUpdateCustomer(): UseMutationResult<unknown, Error, any, unknown> {
  return useMutation<unknown, Error, any>((cartDetails: CartDetails) => {
    const body = mapCartDetailsToUpdateCustomerRequest(cartDetails)
    return updateCustomerRequest(body)
  })
}

export function useOrderDetails(orderId: number): UseQueryResult<unknown, Error> {
  return useQuery<unknown, Error>(['order', orderId], () => getOrderDetailsRequest(orderId), {
    enabled: !!orderId,
  })
}

export function useCreateCustomer(): UseMutationResult<unknown, Error, any, unknown> {
  return useMutation<unknown, Error, any>(createCustomerRequest)
}

export function useCustomerDetails(customerId: number): UseQueryResult<unknown, Error> {
  return useQuery<unknown, Error>(
    ['customer', customerId],
    () => getCustomerDetailsRequest(customerId),
    {
      enabled: !!customerId,
    }
  )
}

export const useCart = () => {
  return useQuery<Cart>(['cartItems'], getCart)
}

export const useGetPostalCodes = (zoneId = 1) => {
  return useQuery<PostalAddress[]>(['postalCodes', zoneId], () => getPostalCodes(), {
    cacheTime: 1000 * 60 * 5,
  })
}

export const useGetDeliveryDetails = (id: string) => {
  return useQuery<Delivery>(['deliveryDetails'], () => getDeliveryDetails(id))
}

export const useAddToCart = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (data: { productId: number; quantity: number }) => addToCart(data.productId, data.quantity),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cartItems'])
      },
    }
  )
}

export const useUpdateCartItem = () => {
  return useMutation((data: { key: string; quantity: number }) =>
    updateCartItem(data.key, data.quantity)
  )
}

export const useRemoveCartItem = () => {
  return useMutation((key: string) => removeCartItem(key), {
    onSuccess: () => {
      queryClient.invalidateQueries(['cartItems'])
    },
  })
}

export const useClearCart = () => {
  return useMutation(clearCart)
}